import type { FetchReviewsParams } from '@shared/apis/review';
import type { FilterConfig } from '@shared/components/Filter.types';
import type { SortOption } from '@shared/components/Filters';
import type { GICSCode } from '@shared/domain/company/GICS.types';
import { gicsTierRank } from '@shared/domain/company/gicsTierRank';
import type {
  BasicReview,
} from '@shared/domain/review';
import type { Company } from '../../authentication/types/types.auth';
import { BookmarkAddTwoTone, GroupsTwoTone } from '@mui/icons-material';
import { Alert, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { fetchReviews } from '@shared/apis/review';
import { ManageFollowedCompaniesButton } from '@shared/components/Buttons/FollowCompaniesButton';
import Filters from '@shared/components/Filters';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext } from '@/shared/contexts/UserContext';
import {
  ReviewDepth,
  ReviewSearchType,
} from '@shared/domain/review';
import { useApi } from '@shared/services/api';
import { fetchAllGicsCodes, fetchCompanies } from '../../company/company-search/api/api';
import ReviewUnlockModal from '../../review/components/DeepDiveReviewCountModal';
import ReviewCard from './ReviewCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
`;

const CenteredBox = styled.div`
  text-align: center;
`;

export interface ReviewSectionProps {
  hideFilters?: boolean;
  parentPageName?: string;
}

function ReviewSection(props: ReviewSectionProps) {
  const { hideFilters = false, parentPageName } = props;
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const api = useApi();
  const location = useLocation();
  const [_searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL search parameters or localStorage. On
  // `my-reviews` the company-name field is a single debounced textfield
  // (so typing filters live) — everywhere else it is a multi-chip input.
  const getInitialFiltersFromURL = () => {
    const urlSearchParams = new URLSearchParams(location.search);
    const companyNameParam = urlSearchParams.getAll('companyName');
    const industryParam = urlSearchParams.getAll('industry');
    const dealAreaParam = urlSearchParams.getAll('dealArea');
    const stakeholderTitleKeywordsParam = urlSearchParams.getAll('stakeholderTitleKeywords');
    const reviewTypeParam = urlSearchParams.get('reviewType');
    const isMyReviews = parentPageName === 'my-reviews';

    return {
      reviewType: reviewTypeParam || (isMyReviews ? ReviewSearchType.MINE : ReviewSearchType.LATEST),
      companyName: isMyReviews
        ? (companyNameParam[0] ?? '')
        : (companyNameParam.length > 0 ? companyNameParam : []),
      industry: industryParam.length > 0 ? industryParam : [],
      dealArea: dealAreaParam.length > 0 ? dealAreaParam : [],
      stakeholderTitleKeywords: stakeholderTitleKeywordsParam.length > 0 ? stakeholderTitleKeywordsParam : [],
    };
  };

  // Check if URL has any filter parameters (excluding reviewType default)
  const hasURLFilterParams = () => {
    const urlSearchParams = new URLSearchParams(location.search);
    const companyNameParam = urlSearchParams.getAll('companyName');
    const industryParam = urlSearchParams.getAll('industry');
    const dealAreaParam = urlSearchParams.getAll('dealArea');
    const stakeholderTitleKeywordsParam = urlSearchParams.getAll('stakeholderTitleKeywords');
    const reviewTypeParam = urlSearchParams.get('reviewType');

    // Check if there are any meaningful filter params in URL
    return companyNameParam.length > 0
      || industryParam.length > 0
      || dealAreaParam.length > 0
      || stakeholderTitleKeywordsParam.length > 0
      || (reviewTypeParam && reviewTypeParam !== (parentPageName === 'my-reviews' ? ReviewSearchType.MINE : ReviewSearchType.LATEST));
  };

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<BasicReview[] | null>(null);
  const [dealInsights, setDealInsights] = useState<BasicReview[]>([]);
  const [_companies, setCompanies] = useState<Company[]>([]);
  const [industries, setIndustries] = useState<GICSCode[]>([]);
  const [navToReviewId, setNavToReviewId] = useState<number>();
  const [deepDiveReviewLimitModal, setDeepDiveReviewLimitModal]
    = useState(false);
  const [navToCompanyName, setNavToCompanyName] = useState<string>('');

  // Initialize filters - try localStorage first if no URL params, then URL params, then defaults
  const getInitialFilters = useCallback(() => {
    if (hasURLFilterParams()) {
      // URL has filter params, use them
      return getInitialFiltersFromURL();
    }

    // No URL filter params, try to load from localStorage
    const storageKey = `${parentPageName}-page_filters`;
    const savedFilters = localStorage.getItem(storageKey);

    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        // Merge with defaults to ensure all required fields exist
        const defaults = getInitialFiltersFromURL();
        return { ...defaults, ...parsedFilters };
      }
      catch (error) {
      }
    }

    // Fallback to URL defaults
    return getInitialFiltersFromURL();
  }, [parentPageName]);

  // Filter state managed by new Filters component, initialized from localStorage or URL parameters
  const [currentFilters, setCurrentFilters] = useState<Record<string, string | string[]>>(getInitialFilters());
  const [filtersInitialized, setFiltersInitialized] = useState(true);

  // Sort state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  // Get active filter values
  const activeReviewType = currentFilters.reviewType as ReviewSearchType;
  const companyNameRaw = currentFilters.companyName;
  // Normalise companyName to an array regardless of whether the page
  // uses a single textfield (my-reviews) or a multi-chip Autocomplete.
  // Memoised so the array reference is stable until the source value
  // changes — otherwise `getReviews`'s useCallback would rebuild every
  // render and re-fire its fetch effect in a loop.
  const companyNameFilter = useMemo<string[]>(() => {
    if (Array.isArray(companyNameRaw)) {
      return companyNameRaw.filter(n => typeof n === 'string' && n.trim() !== '');
    }
    if (typeof companyNameRaw === 'string' && companyNameRaw.trim() !== '') {
      return [companyNameRaw.trim()];
    }
    return [];
  }, [companyNameRaw]);
  const industryFilter = currentFilters.industry as string[];
  const dealAreaFilter = currentFilters.dealArea as string[];
  const stakeholderTitleKeywordsFilter = currentFilters.stakeholderTitleKeywords as string[];

  const isFollowedOnly = activeReviewType === ReviewSearchType.FOLLOWED;
  const isMineOnly = parentPageName === 'my-reviews';

  // Update filters when URL search parameters change, but preserve localStorage values
  // when just switching tabs (no URL filter params)
  useEffect(() => {
    if (hasURLFilterParams()) {
      // URL has explicit filter params, use them and mark as initialized
      const urlFilters = getInitialFiltersFromURL();
      setCurrentFilters(urlFilters);
      setFiltersInitialized(true);
    }
    // If no URL filter params, preserve current filters (which may be from localStorage)
    // and let the component initialization handle loading from localStorage
  }, [location.search, parentPageName]);

  // Fetch industries on mount
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const fetchedGicsCodes = await fetchAllGicsCodes();
        // Include all GICS tiers, not just industries, matching CompanySearchPage and IntelligenceRequestsPage
        setIndustries(fetchedGicsCodes);
      }
      catch (error) {
      }
    };

    loadIndustries();
  }, []);

  const getReviews = useCallback(async () => {
    setLoading(true);
    try {
      // Find companies that match the filters
      const companyIds: number[] = [];

      const params: FetchReviewsParams = {
        authorId: isMineOnly && user?.id ? [user.id] : [],
        companyId: companyIds.length > 0 ? companyIds : undefined,
        companyName: companyNameFilter.length > 0 ? companyNameFilter : undefined,
        dealArea: dealAreaFilter.length > 0 ? dealAreaFilter : undefined,
        followedOnly: isFollowedOnly,
        gicsCode: industryFilter.length > 0 ? industryFilter : undefined,
        limit: 100,
        orderBy: sortBy,
        orderDirection: sortDirection,
        searchType: ReviewSearchType.CUSTOM,
        stakeholderTitleKeywords: stakeholderTitleKeywordsFilter.length > 0 ? stakeholderTitleKeywordsFilter : undefined,
      };

      const response = await fetchReviews(params, user?.idToken || '');
      setReviews(response.data);
    }
    catch (err) {
    }
    finally {
      setLoading(false);
    }
  }, [
    isMineOnly,
    user?.id,
    user?.idToken,
    companyNameFilter,
    dealAreaFilter,
    isFollowedOnly,
    industryFilter,
    sortBy,
    sortDirection,
    stakeholderTitleKeywordsFilter,
  ]);

  useEffect(() => {
    if (user?.idToken) {
      getReviews();
    }
  }, [getReviews]);

  // Fetch deal insights (stub reviews) for the my-reviews page
  useEffect(() => {
    if (isMineOnly && user?.idToken) {
      const fetchDealInsights = async () => {
        try {
          const response = await api.get('/deal-insights');
          if (!response.error && response.data) {
            setDealInsights(response.data);
          }
        } catch {
          // Silent fail - deal insights are supplementary
        }
      };
      fetchDealInsights();
    }
  }, [isMineOnly, user?.idToken]);

  // Combine regular reviews with deal insights for display, sorted by createdAt descending
  const combinedReviews = useMemo(() => {
    if (!isMineOnly || dealInsights.length === 0) return reviews;
    if (!reviews) return dealInsights;
    const existingIds = new Set(reviews.map(r => r.id));
    const newInsights = dealInsights.filter(r => !existingIds.has(r.id));
    const combined = [...reviews, ...newInsights];
    combined.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
    return combined;
  }, [reviews, dealInsights, isMineOnly]);

  const filterConfigs: FilterConfig[] = useMemo(() => {
    const configs: FilterConfig[] = [
      {
        id: 'companyName',
        label: 'Company Name',
        type: 'textfield',
        size: 'small',
        // Single debounced textfield on my-reviews (live filter as you
        // type); multi-chip Autocomplete elsewhere (commit on Enter).
        multiple: parentPageName !== 'my-reviews',
        placeholder: 'Search by company name',
        testId: 'company-name-filter',
        debounceMillis: 300,
      },
    ];

    if (parentPageName !== 'my-reviews') {
      configs.push(
        {
          id: 'industry',
          label: 'Sector / Industry',
          type: 'select',
          autocomplete: true,
          multiple: true,
          clearable: false,
          placeholder: 'Filter on sectors or industries',
          size: 'small',
          options: industries
            .sort((a, b) => {
              if (a.tier === b.tier) {
                return a.name.localeCompare(b.name);
              }
              return gicsTierRank(a.tier) - gicsTierRank(b.tier);
            })
            .map(gics => ({
              value: gics.code,
              name: gics.name,
              group: gics.tier || 'Other',
            })),
          testId: 'industry-filter-select',
        },
        {
          id: 'dealArea',
          label: 'Deal Department',
          type: 'select',
          autocomplete: true,
          multiple: true,
          placeholder: 'Filter on department (Sales, Marketing, IT, ...)',
          clearable: false,
          size: 'small',
          // modalMode not specified, so will go to modal when parent modalMode=true
          options: [
            { value: 'Customer Success', name: 'Customer Success' },
            { value: 'Engineering', name: 'Engineering' },
            { value: 'Finance', name: 'Finance' },
            { value: 'HR', name: 'HR' },
            { value: 'Legal', name: 'Legal' },
            { value: 'Marketing', name: 'Marketing' },
            { value: 'Operations', name: 'Operations' },
            { value: 'Product', name: 'Product' },
            { value: 'Sales', name: 'Sales' },
            { value: 'IT', name: 'IT' },
            { value: 'Security', name: 'Security' },
            { value: 'Transportation', name: 'Transportation' },
          ],
          testId: 'deal-area-filter-select',
        },
        {
          id: 'reviewType',
          label: 'Followed Only?',
          type: 'toggle',
          exclusive: true,
          size: 'small',
          options: [
            {
              value: ReviewSearchType.CUSTOM,
              icon: <GroupsTwoTone />,
              name: 'All Companies',
              default: true,
            },
            {
              value: ReviewSearchType.FOLLOWED,
              name: 'Followed Only',
              icon: <BookmarkAddTwoTone />,
            },
          ],
          showLabel: false,
          modalMode: false,
          testId: 'review-type-filter',
        },
        {
          id: 'stakeholderTitleKeywords',
          label: 'Stakeholder Title Keywords',
          type: 'textfield',
          size: 'small',
          multiple: true,
          placeholder: 'Filter on stakeholder titles',
          testId: 'stakeholder-title-keywords-filter',
          debounceMillis: 300,
        },
      );
    }

    return configs;
  }, [parentPageName, industries]);

  // Sort configuration
  const sortOptions: SortOption[] = useMemo(() => [
    { id: 'createdAt', label: 'Date Created' },
    { id: 'companyName', label: 'Company Name' },
    { id: 'likePercentage', label: 'Rating' },
  ], []);

  // Handle filter changes from the Filters component
  const handleFilterChange = useCallback((filterId: string, value: string | string[]) => {
    setCurrentFilters(prev => ({
      ...prev,
      [filterId]: value,
    }));

    // Mark filters as initialized when they are changed (including from localStorage)
    if (!filtersInitialized) {
      setFiltersInitialized(true);
    }

    // Update URL parameters to match filter changes for bookmarking/sharing
    const newSearchParams = new URLSearchParams(location.search);

    // Remove existing parameters for this filter
    newSearchParams.delete(filterId);

    // Add new values
    if (Array.isArray(value)) {
      value.forEach((val) => {
        if (val && val.trim() !== '') {
          newSearchParams.append(filterId, val);
        }
      });
    }
    else if (value && value.trim() !== '') {
      // Don't add default reviewType to URL to keep URLs clean
      if (!(filterId === 'reviewType'
        && ((parentPageName === 'my-reviews' && value === ReviewSearchType.MINE)
          || (parentPageName !== 'my-reviews' && value === ReviewSearchType.LATEST)))) {
        newSearchParams.set(filterId, value);
      }
    }

    // Update URL without causing a page reload
    const newSearch = newSearchParams.toString();
    if (newSearch !== location.search.slice(1)) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [filtersInitialized, location.search, parentPageName, setSearchParams]);

  // Handle sort changes
  const handleSortChange = useCallback((orderBy: string, orderDirection: 'ASC' | 'DESC') => {
    setSortBy(orderBy);
    setSortDirection(orderDirection);
  }, []);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    setCurrentFilters(prev => ({
      ...prev,
      companyName: [], // Multiple textfield should be cleared to empty array
      dealArea: [],
      industry: [],
      stakeholderTitleKeywords: [], // Multiple textfield should be cleared to empty array
      // Keep reviewType as it's exclusive and should maintain a value
    }));

    // Clear URL parameters as well (except reviewType which should maintain default)
    const newSearchParams = new URLSearchParams();
    const currentReviewType = currentFilters.reviewType as string;

    // Only add reviewType to URL if it's not the default value
    if (!((parentPageName === 'my-reviews' && currentReviewType === ReviewSearchType.MINE)
      || (parentPageName !== 'my-reviews' && currentReviewType === ReviewSearchType.LATEST))) {
      newSearchParams.set('reviewType', currentReviewType);
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [currentFilters.reviewType, parentPageName, setSearchParams]);

  const handleReviewClick = (review: BasicReview) => {
    if (!review || !review.companyName)
      return;

    // Stub reviews (deal insights) don't have a review reader page
    if (review.reviewDepth === ReviewDepth.STUB) {
      navigate(`/company/${encodeURIComponent(review.companyName!)}`);
      return;
    }

    setNavToReviewId(review.id);
    setNavToCompanyName(review.companyName);

    if (review.viewed) {
      navigate(`/review-reader/${encodeURIComponent(review.companyName!)}/${String(review.id)}`);
    }
    else if (
      user?.planDeepDiveReviewLimit
      && review.reviewDepth === ReviewDepth.DEEP_DIVE
      && user?.id !== review.userId
    ) {
      setDeepDiveReviewLimitModal(true);
    }
    else {
      navigate({
        pathname: `/review-reader/${encodeURIComponent(review.companyName!)}/${String(review.id)}`,
        search: `?${createSearchParams({ reviewId: String(review.id) })}`,
      });
    }
  };

  const memoizedFetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchCompanies({});
      setCompanies(response.results);
    }
    catch (error) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.idToken) {
      memoizedFetchCompanies();
    }
  }, [user?.idToken, memoizedFetchCompanies]);

  return (
    <Container>
      {!hideFilters && (
        <Filters
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          onClearAllFilters={handleClearAllFilters}
          sortOptions={sortOptions}
          orderBy={sortBy}
          orderDirection={sortDirection}
          onSortChange={handleSortChange}
          storagePrefix={`${parentPageName}-page`}
          currentFilters={currentFilters}
          sortSize="small"
          modalMode={parentPageName !== 'my-reviews'}
          showLabel={false}
          disableAutoLoad={true}
        />
      )}

      {loading
        ? (
            <LoadingSpinner variant="circular" size="large" message="Loading Deal Retros..." fullScreen />
          )
        : combinedReviews?.length
          ? (
              combinedReviews.map((review, index) => (
                <ReviewCard
                  key={review.id}
                  index={index}
                  review={review}
                  userId={user?.id}
                  filterType={activeReviewType}
                  onGotoCompany={() => {
                    if (review.companyName) {
                      navigate(`/company/${encodeURIComponent(review.companyName)}`);
                    }
                  }}
                  onGoToReview={() => handleReviewClick(review)}
                  showTypeIndicator={true}
                />
              ))
            )
          : (
              <CenteredBox>
                <Alert severity="info" sx={{ alignItems: 'center' }}>
                  <Typography variant="body1" align="center">
                    {companyNameFilter.length > 0 || industryFilter.length > 0
                      ? 'No deal retros found for the selected filters. Please try adjusting your search criteria.'
                      : (isFollowedOnly
                          ? (!user?.followedCompanies || user?.followedCompanies.length === 0)
                              ? (
                                  <>
                                    You aren't following any companies yet!
                                    <ManageFollowedCompaniesButton sx={{ ml: 1, backgroundColor: 'white' }} />
                                  </>
                                )
                              : 'No deal retros found. You can submit an Intelligence Request to get deal retros from other Kandir users.'
                          : (isMineOnly)
                              ? 'You haven\'t posted any deal retros yet.  Check for an Active Intelligence Request or find a company you\'ve sold to and leave a deal retro.'
                              : 'No deal retros found.')}
                  </Typography>
                </Alert>
              </CenteredBox>
            )}

      <ReviewUnlockModal
        open={deepDiveReviewLimitModal}
        onCancel={() => setDeepDiveReviewLimitModal(false)}
        maxConsumableDeepDivesInSubPeriod={
          user?.maxConsumableDeepDivesInSubPeriod ?? null
        }
        consumedDeepDives={user?.deepDivesViewedInSubPeriod ?? 0}
        onProceed={() =>
          navigate(
            `/review-reader/${encodeURIComponent(navToCompanyName)}/${String(navToReviewId)}?unlock=true`,
          )}
        cancelText="Back to Deal Retro Feed"
      />
    </Container>
  );
}

export default ReviewSection;
